<script>
	// Loading first polyfills that are shared between for legacy&modern browsers.
	import 'abortcontroller-polyfill';
	import 'intersection-observer';

	import { onMount } from 'svelte';
	import axios from 'axios';
	import * as Sentry from '@sentry/sveltekit';
	import { frontendConfig } from '$lib/stores';
	import { shouldReport } from '$lib/sentry-filter.js';
	import { scrubEventUrls } from '$lib/sentry-scrub.js';
	import { installCspReporter } from '$lib/csp-report.js';
	import { user } from '$lib/utils.js';
	/**
	 * @typedef {Object} Props
	 * @property {import('svelte').Snippet} [children]
	 */

	/** @type {Props} */
	const { children } = $props();

	let loading = $state(true);

	Sentry.init({
		dsn: 'https://my_dsn@glitchtip.local/1',
		tunnel: '/public/sentry/',
		release: import.meta.env.GIT_REV,
		integrations: [Sentry.browserTracingIntegration()],
		autoSessionTracking: false,
		// Two independent jobs, and they compose in this order for cost: an event we are dropping
		// needs no scrubbing. shouldReport judges whether the event is ours at all (sip-3mb);
		// scrubEventUrls takes the payer's details out of the URL of one we keep (sip-9mr).
		beforeSend: (event) => (shouldReport(event) ? scrubEventUrls(event) : null)
	});

	// A CSP violation is the one payment failure nothing else can see: report-uri is ignored
	// inside a <meta> tag, and script-src/connect-src/frame-src have to live there because only
	// the build knows the hydration script's hash. So if Klarna moves a host, our policy blocks
	// it, the tile greys out, and no log anywhere records why (sip-nq4, sip-9vh).
	//
	// captureMessage rather than captureException on purpose: sentry-filter.js only judges
	// events carrying an unhandled exception, so a message passes it by construction.
	installCspReporter((message, context) =>
		Sentry.captureMessage(message, { level: 'error', extra: context })
	);

	$effect(() => {
		Sentry.getCurrentScope().setExtra('user', {
			uuid: $user?.uuid,
			username: $user?.username,
			is_superuser: $user?.is_superuser,
			roles: $user?.roles
		});
	});

	onMount(() => {
		axios
			.get('/public/config')
			.then((resp) => {
				frontendConfig.set(resp.data);
			})
			.catch((e) => {
				// A dropped or cancelled request means the visitor navigated away or lost
				// their connection mid-load, which is nothing we can act on. Report the rest.
				if (!['ERR_NETWORK', 'ERR_CANCELED', 'ECONNABORTED'].includes(e.code)) {
					Sentry.captureException(e);
				}
			})
			.finally(() => {
				// Render either way. frontendConfig defaults to {} and its consumers cope
				// with that, whereas staying in the loading state leaves the visitor
				// looking at a permanently blank page.
				loading = false;
			});
	});
</script>

{#if !loading}
	{@render children?.()}
{/if}
